import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { CarePlanAnalytics } from '../analytics/care-plan-analytics';
import { CARE_PLAN_CLIENT_CONTEXT } from '../care-plan-client-context.token';
import { ACTION_API, ActionApi } from './api/action-api.token';
import { CreateActionRequest } from './api/action-api.model';
import {
  ActionOwner,
  ActionReferenceData,
  ActionReferenceValue,
  CarePlanAction,
  CreateActionValue,
  UpdateActionValue,
  humanizeActionCode,
} from './actions.model';
import {
  ACTION_OWNER_API,
  ActionOwnerApi,
} from './owners/action-owner-api.token';
import {
  ACTION_REFERENCE_API,
  ActionReferenceApi,
} from './reference/action-reference-api.token';
import {
  ActionEditorContext,
  ActionMutationOptions,
} from './action-editor-source';
import { buildActionUpdateRequest } from './action-update-request';

const PAGE_SIZE = 100;
const OPEN_ACTION_STATUSES = ['OPEN', 'BLOCKED'] as const;

type ActionScope =
  | { kind: 'case'; caseId: string }
  | {
      kind: 'client';
      clientId: number;
      statusCodes: readonly string[];
    };

@Injectable()
export class CarePlanActionsStore {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly _api = inject<ActionApi>(ACTION_API);
  private readonly _references =
    inject<ActionReferenceApi>(ACTION_REFERENCE_API);
  private readonly _ownersApi = inject<ActionOwnerApi>(ACTION_OWNER_API);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _clientContext = inject(CARE_PLAN_CLIENT_CONTEXT, {
    optional: true,
  });
  private readonly _actions = signal<CarePlanAction[]>([]);
  private readonly _actionTypes = signal<ActionReferenceValue[]>([]);
  private readonly _statuses = signal<ActionReferenceValue[]>([]);
  private readonly _priorities = signal<ActionReferenceValue[]>([]);
  private readonly _communicationPreferences = signal<ActionReferenceValue[]>(
    [],
  );
  private readonly _owners = signal<ActionOwner[]>([]);
  private readonly _catalogDependency = signal<string | null>(null);
  private readonly _ownerDependency = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _savingIds = signal<ReadonlySet<string>>(new Set());
  private _scope: ActionScope | null = null;
  private _loadGeneration = 0;

  readonly actions = this._actions.asReadonly();
  readonly actionTypes = this._actionTypes.asReadonly();
  readonly statuses = this._statuses.asReadonly();
  readonly priorities = this._priorities.asReadonly();
  readonly communicationPreferences =
    this._communicationPreferences.asReadonly();
  readonly owners = this._owners.asReadonly();
  readonly catalogDependency = this._catalogDependency.asReadonly();
  readonly ownerDependency = this._ownerDependency.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._actions().length);
  readonly canCreate = computed(
    () =>
      this._actionTypes().length > 0 &&
      this._statuses().some((status) => status.code === 'OPEN') &&
      this._priorities().length > 0 &&
      this._communicationPreferences().length > 0,
  );
  readonly defaultOwner = computed(
    () => this._owners().find((owner) => owner.isCurrentUser) ?? null,
  );

  isSaving(actionId: string): boolean {
    return this._savingIds().has(actionId);
  }

  async load(caseId: string): Promise<void> {
    if (!caseId) return;
    return this._loadScope({ kind: 'case', caseId });
  }

  async loadOpenForClient(clientId: number): Promise<void> {
    if (!Number.isSafeInteger(clientId) || clientId <= 0) return;
    return this._loadScope({
      kind: 'client',
      clientId,
      statusCodes: OPEN_ACTION_STATUSES,
    });
  }

  clear(): void {
    this._scope = null;
    this._loadGeneration += 1;
    this._actions.set([]);
    this._loading.set(false);
    this._error.set(null);
  }

  private async _loadScope(scope: ActionScope): Promise<void> {
    this._scope = scope;
    const generation = ++this._loadGeneration;
    this._loading.set(true);
    this._error.set(null);
    try {
      const [actions, references, ownerData] = await Promise.all([
        this._loadAll(scope),
        firstValueFrom(this._references.loadReferenceData()).catch((error) =>
          this._unavailableReferences(error),
        ),
        firstValueFrom(this._ownersApi.loadOwners()),
      ]);
      const owners = await this._resolveOwners(actions, ownerData.owners);
      if (generation !== this._loadGeneration) return;
      this._actions.set(actions);
      this._actionTypes.set([...references.actionTypes]);
      this._statuses.set([...references.statuses]);
      this._priorities.set([...references.priorities]);
      this._communicationPreferences.set([
        ...references.communicationPreferences,
      ]);
      this._catalogDependency.set(references.dependencyMessage);
      this._owners.set(owners);
      this._ownerDependency.set(ownerData.dependencyMessage);
    } catch (error) {
      if (generation !== this._loadGeneration) return;
      this._error.set(this._message(error));
    } finally {
      if (generation === this._loadGeneration) {
        this._loading.set(false);
      }
    }
  }

  retry(): Promise<void> {
    return this._scope ? this._loadScope(this._scope) : Promise.resolve();
  }

  analyticsContext(action?: CarePlanAction): ActionEditorContext {
    const clientId =
      action?.clientId ?? this._clientContext?.clientId() ?? undefined;
    const clientGuid =
      clientId === this._clientContext?.clientId()
        ? (this._clientContext?.activeGuid() ?? undefined)
        : undefined;
    return {
      clientId,
      clientGuid,
      caseId: action
        ? action.caseId
        : this._scope?.kind === 'case'
          ? this._scope.caseId
          : null,
    };
  }

  actionTypeLabel(code: string | null): string {
    return this._label(this._actionTypes(), code);
  }

  statusLabel(code: string | null): string {
    return this._label(this._statuses(), code);
  }

  communicationLabel(code: string | null): string {
    return this._label(this._communicationPreferences(), code);
  }

  ownerLabel(id: string | null): string {
    if (!id) return 'Owner unavailable';
    return (
      this._owners().find((owner) => owner.id === id)?.displayName ??
      'Financial Assistant'
    );
  }

  getDetail(actionId: string): Promise<CarePlanAction> {
    return firstValueFrom(this._api.getAction(actionId));
  }

  async create(
    value: CreateActionValue,
    options: ActionMutationOptions = {},
  ): Promise<CarePlanAction> {
    return this._actionLogs.run(
      'care_plan.action.create',
      `${this._clientContext?.activeGuid()}:${this._scope?.kind === 'case' ? this._scope.caseId : 'client'}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this._clientContext?.activeGuid(),
          case_id:
            this._scope?.kind === 'case' ? this._scope.caseId : value.caseId,
        };
        logAttempt.stage = 'save';
        const selectedCase =
          this._scope?.kind === 'case' ? this._scope.caseId : value.caseId;
        const caseId = selectedCase
          ? this._requiredId(selectedCase, 'case')
          : undefined;
        const analyticsContext = this._analyticsContext();
        const clientId = this._clientContext?.clientId();
        if (!clientId) throw new Error('Client context is unavailable.');
        if (!this.canCreate()) {
          throw new Error('Action reference data is required.');
        }
        const body: CreateActionRequest = {
          clientId,
          ...(caseId ? { caseId } : {}),
          name: value.title.trim(),
          ...(value.description?.trim()
            ? { description: value.description.trim() }
            : {}),
          ...(value.assignedToId ? { assignedToId: value.assignedToId } : {}),
          actionTypeCode: value.actionTypeCode,
          dueDate: value.dueDate,
          priorityCode: value.priorityCode,
          ...(value.communicationPreferenceCode
            ? { communicationPreferenceCode: value.communicationPreferenceCode }
            : {}),
          clientPromise: value.clientPromise,
        };
        const created = await firstValueFrom(
          logAttempt.request(this._api.createAction(body)),
        );
        logAttempt.completedCount = 1;
        if (analyticsContext && options.eventOwner !== 'modal') {
          this._analytics.actionCreated({
            ...analyticsContext,
            actionId: created.id,
            actionType: created.actionTypeCode,
            priority: created.priorityCode,
            hasDueDate: !!created.dueDate,
            clientPromise: created.clientPromise,
            ownerId: created.assignedToId,
          });
        }
        await this._reloadCurrentScope();
        return created;
      },
      options?.eventOwner !== 'modal',
    );
  }

  async update(
    actionId: string,
    value: UpdateActionValue,
    options: ActionMutationOptions = {},
  ): Promise<CarePlanAction> {
    return this._actionLogs.run(
      value.statusCode !== undefined
        ? 'care_plan.action.change_status'
        : value.assignedToId !== undefined
          ? 'care_plan.action.reassign'
          : 'care_plan.action.update',
      `${this._clientContext?.activeGuid()}:${actionId}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this._clientContext?.activeGuid(),
          action_id: actionId,
          case_id: this._actions().find((action) => action.id === actionId)
            ?.caseId,
        };
        logAttempt.stage = 'save';
        const previous = this._actions().find(
          (action) => action.id === actionId,
        );
        const analyticsContext = this._analyticsContext(previous?.caseId);
        const body = buildActionUpdateRequest(value);
        return this._mutate(
          actionId,
          () => logAttempt.request(this._api.updateAction(actionId, body)),
          (updated) => {
            logAttempt.completedCount = 1;
            if (
              options.eventOwner === 'modal' ||
              !analyticsContext ||
              !previous
            )
              return;
            if (
              value.statusCode !== undefined &&
              previous.statusCode !== updated.statusCode
            ) {
              this._analytics.actionStatusChanged({
                ...analyticsContext,
                actionId,
                actionType: updated.actionTypeCode,
                fromStatus: previous.statusCode,
                toStatus: updated.statusCode,
              });
            }
            if (
              value.assignedToId !== undefined &&
              previous.assignedToId !== updated.assignedToId
            ) {
              this._analytics.actionReassigned({
                ...analyticsContext,
                actionId,
                actionType: updated.actionTypeCode,
                fromOwnerId: previous.assignedToId,
                toOwnerId: updated.assignedToId,
              });
            }
          },
        );
      },
      options?.eventOwner !== 'modal',
    );
  }

  transitionStatus(
    actionId: string,
    statusCode: string,
  ): Promise<CarePlanAction> {
    return this.update(actionId, { statusCode });
  }

  private async _loadAll(scope: ActionScope): Promise<CarePlanAction[]> {
    const all: CarePlanAction[] = [];
    let offset = 0;
    while (true) {
      const page = await firstValueFrom(
        scope.kind === 'case'
          ? this._api.listActions(scope.caseId, PAGE_SIZE, offset)
          : this._api.listClientActions(
              scope.clientId,
              scope.statusCodes,
              PAGE_SIZE,
              offset,
            ),
      );
      all.push(...page.items);
      offset += page.consumedItems;
      if (offset >= page.pagination.totalItems || page.consumedItems === 0)
        return all;
    }
  }

  private async _mutate(
    actionId: string,
    request: () => ReturnType<ActionApi['updateAction']>,
    afterSuccess?: (result: CarePlanAction) => void,
  ): Promise<CarePlanAction> {
    this._setSaving(actionId, true);
    try {
      const result = await firstValueFrom(request());
      afterSuccess?.(result);
      await this._reloadCurrentScope();
      return result;
    } finally {
      this._setSaving(actionId, false);
    }
  }

  private _analyticsContext(caseId?: string | null): {
    clientGuid: string;
    caseId: string;
  } | null {
    const clientGuid = this._clientContext?.activeGuid();
    const scopedCaseId =
      caseId ?? (this._scope?.kind === 'case' ? this._scope.caseId : null);
    if (!clientGuid || !scopedCaseId) return null;
    return { clientGuid, caseId: scopedCaseId };
  }

  private async _reloadCurrentScope(): Promise<void> {
    if (this._scope) await this._loadScope(this._scope);
  }

  private async _resolveOwners(
    actions: readonly CarePlanAction[],
    initialOwners: readonly ActionOwner[],
  ): Promise<ActionOwner[]> {
    const owners = new Map(initialOwners.map((owner) => [owner.id, owner]));
    const unresolvedIds = [
      ...new Set(
        actions
          .filter((action) => action.sourceTypeCode === 'HUMAN_FA')
          .map((action) => action.assignedToId)
          .filter((id): id is string => !!id && !owners.has(id)),
      ),
    ];
    const resolved = await Promise.all(
      unresolvedIds.map((id) =>
        firstValueFrom(
          this._ownersApi.resolveOwner(id).pipe(catchError(() => of(null))),
        ),
      ),
    );
    resolved.forEach((owner) => {
      if (owner) owners.set(owner.id, owner);
    });
    return [...owners.values()];
  }

  private _requiredId(value: string | null, kind: string): number {
    const id = Number(value);
    if (!Number.isSafeInteger(id) || id <= 0)
      throw new Error(`A valid ${kind} id is required.`);
    return id;
  }

  private _label(
    values: readonly ActionReferenceValue[],
    code: string | null,
  ): string {
    return (
      values.find((value) => value.code === code)?.label ??
      humanizeActionCode(code)
    );
  }

  private _unavailableReferences(error: unknown): ActionReferenceData {
    return {
      actionTypes: [],
      statuses: [],
      priorities: [],
      communicationPreferences: [],
      dependencyMessage: this._message(error),
    };
  }

  private _setSaving(actionId: string, saving: boolean): void {
    this._savingIds.update((current) => {
      const next = new Set(current);
      if (saving) next.add(actionId);
      else next.delete(actionId);
      return next;
    });
  }

  private _message(error: unknown): string {
    return error instanceof Error
      ? error.message
      : "Actions couldn't be loaded right now.";
  }
}
